import { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import Card from '@perpay-web/components/base/Card/Card';
import Clickable from '@perpay-web/components/src/base/Clickable/Clickable';
import {
    maybeBase64EncodeWithHandler,
    isFilePdf,
} from '@perpay-web/fintech/utils/fileUtils';
import { getClassName } from '@perpay-web/utils/domUtils';

import styles from './CardUploadDocumentSection.scss';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    // eslint-disable-next-line es-x/no-import-meta
    import.meta.url,
).toString();

const minFileSize = 5000;
const maxFileSize = 10485760;
const validFileTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
];

const getFileError = (file) => {
    if (!file) return null;
    if (file.size <= minFileSize) {
        return 'File size is too small, please upload a new version of the document';
    }
    if (file.size >= maxFileSize) {
        return 'File size is too large, please upload a new version of the document';
    }
    if (!validFileTypes.includes(file.type)) {
        return 'File must have one of these formats: JPEG, JPG, PNG or PDF';
    }
    return null;
};

const CardWithoutPreview = ({ title, subtitle, icon, copy }) => (
    <>
        <div className={styles['upload-section-icon']}>
            <span className={styles[icon]} />
        </div>
        <div className={styles['upload-section-copy-section']}>
            <div className={styles['upload-section-heading']}>
                <strong>{title}</strong>
                {subtitle ? (
                    <span className={styles['upload-section-subtitle']}>
                        <em>{subtitle}</em>
                    </span>
                ) : null}
            </div>
            <div className={styles['upload-section-copy']}>{copy}</div>
        </div>
        <div className={styles['action-icon-wrapper']}>
            <span className={styles['chevron-right']} />
        </div>
    </>
);

const CardWithPreview = ({
    title,
    imageData = null,
    file = null,
    error = '',
}) => (
    <>
        <div className={styles['preview-wrapper']}>
            {imageData ? (
                <img
                    src={imageData}
                    alt={`${title} preview`}
                    className={styles.img}
                />
            ) : (
                <Document loading={null} file={file}>
                    <Page
                        loading={null}
                        pageNumber={1}
                        height={50}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                    />
                </Document>
            )}
        </div>
        <div className={styles['preview-title-and-reupload']}>
            <div className={styles['preview-title-wrapper']}>
                <strong>{title}</strong>
                {!error && <span className={styles['check-icon']} />}
            </div>
            <div className={styles['file-name']}>{file ? file.name : ''}</div>
        </div>
        <div className={styles['action-icon-wrapper']}>
            <span className={styles['chevron-right']} />
        </div>
    </>
);

const CardUploadDocumentSection = ({
    title,
    subtitle,
    copy,
    icon,
    file = null,
    onSelectFile,
    onClickUpload,
    error = '',
    onError,
}) => {
    const [imageData, setImageData] = useState(null);
    const [isImageEncoded, setIsImageEncoded] = useState(false);

    const onImageEncoded = useCallback((base64Data) => {
        setImageData(base64Data);
        setIsImageEncoded(true);
    }, []);

    // if file is changed, check if file is an image and encode & get data for
    // preview
    useEffect(() => {
        let fileReader;
        const isPdf = isFilePdf(file);

        const newFileError = getFileError(file);
        onError(newFileError);

        if (file && !isPdf) {
            fileReader = maybeBase64EncodeWithHandler(file, onImageEncoded);
        } else {
            setImageData(null);
            setIsImageEncoded(false);
        }
        return () => {
            if (fileReader) fileReader.abort();
        };
    }, [file, onError, onImageEncoded]);

    const fileInputRef = useRef(null);
    const openFileDialog = () => {
        onClickUpload();
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const fileChanged = () => {
        if (fileInputRef.current) {
            const newFile = fileInputRef.current.files[0];
            onSelectFile(newFile);
        }
    };

    // show preview of image if img data exists or if file exists and is PDF
    const showPreview =
        (imageData && isImageEncoded) || (file && isFilePdf(file));

    return (
        <Clickable
            clickForRef={fileInputRef}
            onClick={openFileDialog}
            className={styles['card-wrapper']}
        >
            <Card
                className={getClassName(
                    styles.card,
                    error && styles['card-error'],
                )}
            >
                {showPreview ? (
                    <CardWithPreview
                        title={title}
                        imageData={imageData}
                        file={file}
                        error={error}
                    />
                ) : (
                    <CardWithoutPreview
                        title={title}
                        subtitle={subtitle}
                        copy={copy}
                        icon={icon}
                    />
                )}
                <input
                    hidden
                    type='file'
                    ref={fileInputRef}
                    onChange={fileChanged}
                    accept='image/*,application/pdf'
                />
            </Card>
            {error ? (
                <div className={styles['error-message']}>
                    <em>{error}</em>
                </div>
            ) : null}
        </Clickable>
    );
};

export default CardUploadDocumentSection;
